/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */
import type { Company } from '@/types/dashboard-types'

/**
 * Builds the parent-company chain for a company (immediate parent up to the root) and returns
 * the ancestor names to display, applying two display rules:
 *   1. Drop the topmost company — everyone is under it, so it adds no information.
 *   2. Drop "Cooltrax"-prefixed companies that sit directly under the root (e.g. "Cooltrax
 *      Asia Pacific"), which are internal corporate layers rather than meaningful customers.
 *
 * Each company references its parent by `parentId`, resolved against the full company list.
 */
export const companyAncestorNames = (company: Company, companies: Company[]): string[] => {
  const byId = new Map<number, Company>(companies.map((c) => [c.id, c]))

  const chain: Company[] = []
  const seen = new Set<number>([company.id])
  let current: Company | undefined = company
  while (current?.parentId != null && !seen.has(current.parentId)) {
    const parent = byId.get(current.parentId)
    if (!parent) break // ancestor not in the accessible list — stop walking
    seen.add(parent.id)
    chain.push(parent)
    current = parent
  }

  if (chain.length === 0) return []

  const root = chain[chain.length - 1]
  return chain
    .slice(0, -1) // rule 1: drop the topmost company
    .filter((c) => {
      // rule 2: drop Cooltrax-prefixed companies directly under the root
      const isDirectChildOfRoot = c.parentId === root.id
      return !(isDirectChildOfRoot && c.name.startsWith('Cooltrax'))
    })
    .map((c) => c.name)
}

/** e.g. "Bega/Linfox FMCG/Linfox Australia" (empty string when there is nothing to show). */
export const companyHierarchyString = (company: Company, companies: Company[]): string =>
  companyAncestorNames(company, companies).join('/')

/** e.g. "Bega NSW - CC 2770 (Bega/Linfox FMCG/Linfox Australia)". */
export const companyLabelWithHierarchy = (company: Company, companies: Company[]): string => {
  const hierarchy = companyHierarchyString(company, companies)
  return hierarchy ? `${company.name} (${hierarchy})` : company.name
}
